import { useMemo, useState } from 'react';
import type { DrilldownItem, ScopeInfo, ScopeMap } from '../types';
import {
  getFallbackActiveScope,
  getVisibleDrillScopeKeys,
  mergeScopes,
} from '../utils/insightsScopeUtils';

type UseInsightsDrilldownStateArgs = {
  baseScopes: ScopeMap;
  onClose: () => void;
};

export function useInsightsDrilldownState({
  baseScopes,
  onClose,
}: UseInsightsDrilldownStateArgs) {
  const [selectedItem, setSelectedItem] = useState<DrilldownItem>();
  const [selectedTypeIcon, setSelectedTypeIcon] = useState<any>();
  const [activeScopeParam, setActiveScopeParam] = useState<string | undefined>();
  const [baseScopesState, setBaseScopesState] = useState<ScopeMap>({});
  const [drillScopes, setDrillScopes] = useState<ScopeMap>({});
  const [scopeInfoByParam, setScopeInfoByParam] = useState<
    Record<string, ScopeInfo>
  >({});

  const effectiveScopes = useMemo(
    () => mergeScopes(baseScopesState, drillScopes),
    [baseScopesState, drillScopes]
  );

  const openDrilldown = (
    scopeParamKey: string,
    item: DrilldownItem,
    nextTypeIcon: any
  ) => {
    setBaseScopesState(baseScopes);
    setDrillScopes({ [scopeParamKey]: item.id });
    setScopeInfoByParam({
      [scopeParamKey]: {
        id: item.id,
        name: item.name,
        icon: nextTypeIcon,
      },
    });
    setSelectedItem(item);
    setSelectedTypeIcon(nextTypeIcon);
    setActiveScopeParam(scopeParamKey);
  };

  const addDrillScope = (
    scopeParamKey: string,
    item: DrilldownItem,
    nextTypeIcon: any
  ) => {
    setDrillScopes((prev) => ({ ...prev, [scopeParamKey]: item.id }));
    setScopeInfoByParam((prev) => ({
      ...prev,
      [scopeParamKey]: {
        id: item.id,
        name: item.name,
        icon: nextTypeIcon,
      },
    }));
    setSelectedItem(item);
    setSelectedTypeIcon(nextTypeIcon);
    setActiveScopeParam(scopeParamKey);
  };

  const removeDrillScope = (scopeParamKey: string) => {
    const activeScopeKeys = getVisibleDrillScopeKeys(drillScopes);
    if (activeScopeKeys.length <= 1) {
      onClose();
      return;
    }

    const nextDrillScopes = { ...drillScopes };
    delete nextDrillScopes[scopeParamKey];
    setDrillScopes(nextDrillScopes);

    const nextScopeInfoByParam = { ...scopeInfoByParam };
    delete nextScopeInfoByParam[scopeParamKey];
    setScopeInfoByParam(nextScopeInfoByParam);

    if (activeScopeParam === scopeParamKey) {
      const fallbackScopeParam = getFallbackActiveScope(
        scopeParamKey,
        nextDrillScopes
      );
      if (!fallbackScopeParam) {
        onClose();
        return;
      }

      setActiveScopeParam(fallbackScopeParam);
      const fallbackInfo = nextScopeInfoByParam[fallbackScopeParam];
      if (fallbackInfo) {
        setSelectedItem((prev) => ({
          ...(prev ?? { id: fallbackInfo.id, name: fallbackInfo.name }),
          id: fallbackInfo.id,
          name: fallbackInfo.name,
        }));
        setSelectedTypeIcon(fallbackInfo.icon);
      }
    }
  };

  return {
    selectedItem,
    selectedTypeIcon,
    activeScopeParam,
    baseScopesState,
    drillScopes,
    scopeInfoByParam,
    effectiveScopes,
    openDrilldown,
    addDrillScope,
    removeDrillScope,
  };
}
